import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { track, identify, resetIdentity } from '../observability';
import tenantPaymentService from '../services/tenantPaymentService';
import TenantImpersonationBanner from '../components/TenantImpersonationBanner';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import { t, useLang, LangToggle } from '../portal/i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

const PortalDashboard = () => {
  useLang();  // re-render on EN/ES toggle
  const [tenant, setTenant] = useState(null);
  const [rentals, setRentals] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rentals');
  // Profile (My Info tab) state — loaded lazily on first tab open.
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);
  const [profileError, setProfileError] = useState(null);
  // Invoice-detail modal: stores the _id of the row to open, null = closed.
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  // Documents + Contact tabs (lazy-loaded on first open).
  const [documents, setDocuments] = useState(null);   // {documents, gate_note}
  const [messages, setMessages] = useState(null);
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [msgNotice, setMsgNotice] = useState(null);
  // Rent-another-unit picker (owner, 2026-08-06): in-portal now — the old
  // link dumped a signed-in tenant into the anonymous website wizard to
  // retype everything. Same engine server-side; account fields come from
  // the session.
  const [rentModalOpen, setRentModalOpen] = useState(false);
  const [rentOptions, setRentOptions] = useState(null);
  const [rentTypeId, setRentTypeId] = useState(null);
  const [rentUnitId, setRentUnitId] = useState('');
  const [rentBusy, setRentBusy] = useState(false);
  const [rentError, setRentError] = useState(null);
  // Move-out notice inline form: rental _id whose form is open, null = closed.
  const [noticeFormRental, setNoticeFormRental] = useState(null);
  const [noticeDate, setNoticeDate] = useState('');
  const [noticeNote, setNoticeNote] = useState('');
  const [noticeBusy, setNoticeBusy] = useState(false);
  const [noticeError, setNoticeError] = useState(null);
  const navigate = useNavigate();

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('tenantAccessToken');
    if (!token) { navigate('/portal'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Lease fetch must carry the tenant Bearer token, so stream it as a blob
  // rather than a plain link (the file route is auth-scoped).
  const openLease = useCallback(async (rentalId) => {
    const token = localStorage.getItem('tenantAccessToken');
    if (!token) { navigate('/portal'); return; }
    try {
      const res = await axios.get(
        `${API_URL}/api/tenant/rentals/${rentalId}/lease`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate('/portal');
    }
  }, [navigate]);

  // Sign an unsigned lease from the portal — mints a signing token for the
  // tenant's own rental and routes to the same /lease/sign/:token page the
  // public rent wizard uses.
  const signLease = useCallback(async (rentalId) => {
    const token = localStorage.getItem('tenantAccessToken');
    if (!token) { navigate('/portal'); return; }
    try {
      const res = await axios.post(
        `${API_URL}/api/tenant/rentals/${rentalId}/lease/sign-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.signing_token) {
        navigate(`/lease/sign/${res.data.signing_token}`);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate('/portal');
    }
  }, [navigate]);

  const openRentModal = useCallback(async () => {
    const headers = getHeaders();
    if (!headers) return;
    setRentModalOpen(true);
    setRentError(null);
    try {
      const res = await axios.get(`${API_URL}/api/tenant/rent/options`, { headers });
      setRentOptions(res.data);
      const first = res.data?.unit_types?.[0];
      setRentTypeId(first?.id || null);
      setRentUnitId(first?.units?.[0]?.id || '');
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) { navigate('/portal'); return; }
      setRentError(err.response?.data?.detail || 'Could not load available units.');
    }
  }, [getHeaders, navigate]);

  const startRent = useCallback(async () => {
    const headers = getHeaders();
    if (!headers || !rentTypeId || !rentUnitId) return;
    setRentBusy(true);
    setRentError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/tenant/rent/draft`,
        { unit_type_id: rentTypeId, unit_id: rentUnitId },
        { headers },
      );
      track('tenant_rent_another_unit_draft');
      if (res.data?.signing_token) {
        navigate(`/lease/sign/${res.data.signing_token}`);
      } else {
        // Lease automation declined (e.g. no template pinned) — the draft
        // exists; the unit card's Sign lease button picks it up.
        window.location.reload();
      }
    } catch (err) {
      setRentError(err.response?.data?.detail || 'Could not start the rental.');
      setRentBusy(false);
    }
  }, [getHeaders, navigate, rentTypeId, rentUnitId]);

  const logout = useCallback(() => {
    localStorage.removeItem('tenantAccessToken');
    localStorage.removeItem('tenantRefreshToken');
    localStorage.removeItem('tenantCustomer');
    resetIdentity();
    navigate('/portal');
  }, [navigate]);

  useEffect(() => {
    const headers = getHeaders();
    if (!headers) return;
    Promise.all([
      axios.get(`${API_URL}/api/tenant/me`, { headers }),
      axios.get(`${API_URL}/api/tenant/rentals`, { headers }),
      axios.get(`${API_URL}/api/tenant/payments`, { headers }),
      axios.get(`${API_URL}/api/tenant/invoices`, { headers }),
    ])
      .then(([meRes, rentalsRes, paymentsRes, invoicesRes]) => {
        setTenant(meRes.data); setRentals(rentalsRes.data); setPayments(paymentsRes.data); setInvoices(invoicesRes.data);
        identify(meRes.data?._id);
        track('tenant_portal_viewed');
      })
      .catch((err) => { if (err.response?.status === 401 || err.response?.status === 403) logout(); })
      .finally(() => setLoading(false));
  }, [getHeaders, logout]);

  const loadProfile = useCallback(async () => {
    const headers = getHeaders();
    if (!headers) return;
    try {
      const res = await axios.get(`${API_URL}/api/tenant/me/profile`, { headers });
      setProfile(res.data);
      setProfileForm(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) logout();
    }
  }, [getHeaders, logout]);

  // Lazy-load profile the first time the My Info tab opens.
  useEffect(() => {
    if (activeTab === 'profile' && profile === null) {
      loadProfile();
    }
  }, [activeTab, profile, loadProfile]);

  // Lazy-load Documents / Contact data on first tab open.
  useEffect(() => {
    const headers = getHeaders();
    if (!headers) return;
    if (activeTab === 'documents' && documents === null) {
      axios.get(`${API_URL}/api/tenant/documents`, { headers })
        .then((res) => setDocuments(res.data))
        .catch(() => setDocuments({ documents: [], gate_note: null }));
    }
    if (activeTab === 'contact' && messages === null) {
      axios.get(`${API_URL}/api/tenant/messages`, { headers })
        .then((res) => setMessages(res.data))
        .catch(() => setMessages([]));
    }
  }, [activeTab, documents, messages, getHeaders]);

  const downloadSignedDoc = async (docId, label) => {
    const headers = getHeaders();
    if (!headers) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/tenant/documents/signed/${docId}.pdf`,
        { headers, responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(label || 'document').replace(/[^a-z0-9 -]/gi, '')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) logout();
    }
  };

  const sendMessage = async () => {
    const headers = getHeaders();
    if (!headers || !msgBody.trim()) return;
    setMsgSending(true);
    setMsgNotice(null);
    try {
      await axios.post(`${API_URL}/api/tenant/messages`,
        { subject: msgSubject.trim() || undefined, body: msgBody.trim() },
        { headers });
      setMessages([{ subject: msgSubject.trim() || 'Message from tenant portal',
                     body: msgBody.trim(),
                     sent_at: new Date().toISOString() },
                   ...(messages || [])]);
      setMsgSubject('');
      setMsgBody('');
      setMsgNotice({ ok: true, text: t('Message sent — the office will get back to you.') });
    } catch (err) {
      setMsgNotice({ ok: false,
        text: err.response?.data?.detail || 'Could not send the message.' });
    } finally {
      setMsgSending(false);
    }
  };

  // Download a branded PDF receipt for one payment (tenant-scoped endpoint).
  const downloadReceipt = async (paymentId, date) => {
    const headers = getHeaders();
    if (!headers) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/tenant/payments/${paymentId}/receipt.pdf`,
        { headers, responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${date || paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) logout();
    }
  };

  // Tenant vacate notice: give (POST) / cancel (DELETE), then refresh the
  // rental rows in place so the card reflects the scheduled date.
  const giveMoveOutNotice = async (rentalId) => {
    const headers = getHeaders();
    if (!headers || !noticeDate) return;
    setNoticeBusy(true);
    setNoticeError(null);
    try {
      await axios.post(
        `${API_URL}/api/tenant/rentals/${rentalId}/move-out-notice`,
        { move_out_date: noticeDate, note: noticeNote || undefined },
        { headers },
      );
      setRentals(rentals.map((r) => r._id === rentalId
        ? { ...r, scheduled_move_out_date: noticeDate, moveout_notice_source: 'tenant_portal' }
        : r));
      setNoticeFormRental(null);
      setNoticeDate('');
      setNoticeNote('');
    } catch (err) {
      setNoticeError(err.response?.data?.detail || 'Could not submit the notice.');
    } finally {
      setNoticeBusy(false);
    }
  };

  const cancelMoveOutNotice = async (rentalId) => {
    const headers = getHeaders();
    if (!headers) return;
    setNoticeBusy(true);
    try {
      await axios.delete(
        `${API_URL}/api/tenant/rentals/${rentalId}/move-out-notice`,
        { headers },
      );
      setRentals(rentals.map((r) => r._id === rentalId
        ? { ...r, scheduled_move_out_date: null, moveout_notice_source: null }
        : r));
    } catch (err) {
      setNoticeError(err.response?.data?.detail || 'Could not cancel the notice.');
    } finally {
      setNoticeBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <p className="text-[13px] text-slate-400">Unable to load your account.</p>
      </div>
    );
  }

  const totalBalance = rentals.reduce((sum, r) => sum + (r.balance || 0), 0);
  const activeRentals = rentals.filter((r) => r.status === 'Active');

  const tabs = [
    { id: 'rentals', label: t('My Units'), count: activeRentals.length },
    { id: 'payments', label: t('Payments'), count: payments.length },
    { id: 'invoices', label: t('Invoices'), count: invoices.length },
    { id: 'documents', label: t('Documents') },
    { id: 'contact', label: t('Contact Us') },
    { id: 'profile', label: t('My Info') },
  ];

  const saveProfile = async () => {
    const headers = getHeaders();
    if (!headers || !profileForm) return;
    setProfileSaving(true);
    setProfileError(null);
    setProfileMessage(null);
    // Build a partial payload of CHANGED editable fields only — sending
    // the full snapshot would resend the read-only identity fields, which
    // the backend ignores, but trimming keeps the audit log clean.
    const editable = [
      'email', 'phone', 'address', 'city', 'state', 'zip_code',
      'emergency_contact_name', 'emergency_contact_phone',
      'preferred_contact_method',
    ];
    const payload = {};
    editable.forEach((k) => {
      if ((profileForm[k] || '') !== (profile?.[k] || '')) {
        payload[k] = profileForm[k] || null;
      }
    });
    if (Object.keys(payload).length === 0) {
      setProfileMessage('No changes to save.');
      setProfileSaving(false);
      return;
    }
    try {
      const res = await axios.patch(`${API_URL}/api/tenant/me/profile`, payload, { headers });
      setProfile(res.data);
      setProfileForm(res.data);
      setProfileMessage('Profile updated.');
      track('tenant_profile_updated');
    } catch (err) {
      setProfileError(err.response?.data?.detail || 'Could not save profile.');
    } finally {
      setProfileSaving(false);
    }
  };


  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      <TenantImpersonationBanner />
      <header className="bg-white border-b border-slate-200/80">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-[15px] font-semibold text-slate-900">{tenant.facility_name || t('Tenant Portal')}</h1>
            <p className="text-[13px] text-slate-400">Welcome, {tenant.full_name}</p>
          </div>
          <LangToggle />
          <button onClick={logout} className="text-[13px] text-slate-400 hover:text-slate-600 font-medium transition-colors">
            {t('Sign out')}
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid gap-3 md:grid-cols-3 mb-6">
          <div className="rounded-xl border border-slate-200/80 bg-white p-4">
            <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Active Rentals</p>
            <p className="text-xl font-semibold text-slate-900 mt-1">{activeRentals.length}</p>
          </div>
          <div className="rounded-xl border border-slate-200/80 bg-white p-4">
            <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">{t('Account Balance')}</p>
            <p className={`text-xl font-semibold mt-1 ${totalBalance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              ${totalBalance.toFixed(2)}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200/80 bg-white p-4">
            <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">{t('Autopay')}</p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xl font-semibold text-slate-900">{tenant.auto_pay_enabled ? t('On') : t('Off')}</p>
              {tenant.auto_pay_enabled ? (
                <span className="flex items-center gap-3">
                  {/* In-place card replacement — same setup flow, webhook
                      overwrites the saved method. No disable+re-enroll. */}
                  <button
                    onClick={() => navigate('/portal/autopay?update=1')}
                    className="text-[12px] text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {t('Update card')}
                  </button>
                  <button
                    onClick={async () => {
                      await tenantPaymentService.disableAutopay();
                      setTenant({ ...tenant, auto_pay_enabled: false });
                    }}
                    className="text-[12px] text-slate-400 hover:text-red-500 font-medium"
                  >
                    {t('Turn off')}
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => navigate('/portal/autopay')}
                  className="text-[12px] text-blue-600 hover:text-blue-700 font-medium"
                >
                  {t('Set up')}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="border-b border-slate-200/80 mb-4">
          <nav className="flex gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-2.5 text-[13px] font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                {tab.label}{typeof tab.count === 'number' ? ` (${tab.count})` : ''}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'rentals' && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={openRentModal}
                className="text-[12px] font-medium text-blue-600 hover:text-blue-700"
              >
                {t('+ Rent another unit')}
              </button>
            </div>
            {rentals.length === 0 && <p className="text-[13px] text-slate-400">{t('No rentals found.')}</p>}
            {/* Pay all: one-click for a tenant with a balance on 2+ units.
                Backend sums the balances and spreads the charge across every
                open invoice. */}
            {(() => {
              const payableRentals = rentals.filter((r) => (r.balance || 0) > 0);
              const totalBalance = payableRentals.reduce((s, r) => s + (r.balance || 0), 0);
              if (payableRentals.length < 2) return null;
              return (
                <div className="rounded-xl border border-blue-200/80 bg-blue-50/60 p-4 flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-semibold text-slate-900">
                      Total balance across {payableRentals.length} units
                    </p>
                    <p className="text-[12px] text-slate-500">{t('Pay everything in one step.')}</p>
                  </div>
                  <button
                    onClick={() => navigate(`/portal/pay?payAll=1&amount=${totalBalance.toFixed(2)}`)}
                    className="rounded-lg bg-blue-600 text-white text-[13px] font-semibold px-5 py-2 hover:bg-blue-700 transition-colors"
                  >
                    {t('Pay all')} ${totalBalance.toFixed(2)}
                  </button>
                </div>
              );
            })()}
            {rentals.map((r) => (
              <div key={r._id} className="rounded-xl border border-slate-200/80 bg-white p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[13px] font-semibold text-slate-900">
                    Unit {r.unit_number || '—'} ({r.unit_type || '—'})
                  </span>
                  <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                    r.status === 'Active' ? 'bg-emerald-50 text-emerald-600' :
                    r.status === 'Delinquent' ? 'bg-red-50 text-red-500' :
                    'bg-slate-50 text-slate-500'
                  }`}>
                    {r.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[13px]">
                  <div><span className="text-slate-400">{t('Rate:')}</span> <span className="text-slate-600">${r.monthly_rate}/mo</span></div>
                  <div><span className="text-slate-400">{t('Started:')}</span> <span className="text-slate-600">{r.start_date || '—'}</span></div>
                  <div><span className="text-slate-400">{t('Balance:')}</span> <span className={r.balance > 0 ? 'text-red-500 font-medium' : 'text-slate-600'}>${(r.balance || 0).toFixed(2)}</span></div>
                  <div><span className="text-slate-400">{t('Next due:')}</span> <span className="text-slate-600">{r.next_due_date || '—'}</span></div>
                </div>
                {/* The office has paused this rental while it confirms the
                    tenant's reported move-out. Shown only while the hold is
                    live, so it always describes now. No amounts here — the
                    balance above is the computed number. */}
                {r.paused && (
                  <div
                    className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
                    data-testid={`portal-paused-${r._id}`}
                  >
                    <strong>{t('We are confirming your move-out.')}</strong>{' '}
                    {t('Billing and reminders for this unit are on hold while we check.')}{' '}
                    {r.balance > 0 ? t('You can still pay any balance shown here.') : ''}
                  </div>
                )}
                {(() => {
                  // Per-facility lease→pay gate. When the facility has
                  // require_lease_signing === false (brownfield / migrated
                  // facilities mid-cutover) the dashboard NEVER forces a lease
                  // before paying — existing tenants signed on paper / the
                  // prior PMS. Otherwise (default) a new onboarding tenant must
                  // sign before Pay unlocks.
                  const requireLease = tenant?.require_lease_signing !== false;
                  const leaseSigned = r.lease_status === 'signed';
                  const gatePayOnSigning = requireLease && !leaseSigned;
                  const showSignLease = requireLease && !leaseSigned;
                  if (!(r.balance > 0 || r.lease_status)) return null;
                  return (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3">
                    {showSignLease && (
                      <button
                        onClick={() => signLease(r._id)}
                        className="rounded-lg bg-emerald-600 text-white text-[12px] font-semibold px-4 py-1.5 ring-2 ring-emerald-300 ring-offset-1 hover:bg-emerald-700 transition-colors"
                      >
                        {t('Sign lease')}
                      </button>
                    )}
                    {r.balance > 0 && (
                      <button
                        onClick={() => navigate(`/portal/pay?rental=${r._id}&amount=${r.balance}`)}
                        disabled={gatePayOnSigning}
                        title={gatePayOnSigning ? 'Sign your lease before paying' : undefined}
                        className={`rounded-lg text-[12px] font-medium px-4 py-1.5 transition-colors ${
                          !gatePayOnSigning
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        }`}
                      >
                        Pay ${(r.balance || 0).toFixed(2)}
                      </button>
                    )}
                    {leaseSigned && (
                      <button
                        onClick={() => openLease(r._id)}
                        className="text-[12px] text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {t('View lease')}
                      </button>
                    )}
                  </div>
                  );
                })()}
                {/* Move-out notice: facility OPT-IN feature (default off —
                    most facilities take move-outs by phone so their check-out
                    process runs: inspection, cleanout photos on the record).
                    A scheduled date still displays even when the form is off. */}
                {(tenant?.allow_moveout_notice || r.scheduled_move_out_date) &&
                 (r.status === 'Active' || r.status === 'Delinquent') && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    {r.scheduled_move_out_date ? (
                      <div className="flex items-center justify-between">
                        <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-50 text-amber-700">
                          Move-out scheduled: {r.scheduled_move_out_date}
                        </span>
                        {r.moveout_notice_source === 'tenant_portal' && (
                          <button
                            onClick={() => cancelMoveOutNotice(r._id)}
                            disabled={noticeBusy}
                            className="text-[12px] text-slate-400 hover:text-red-500 font-medium disabled:opacity-50"
                          >
                            {t('Cancel notice')}
                          </button>
                        )}
                      </div>
                    ) : noticeFormRental === r._id ? (
                      <div className="space-y-2">
                        <p className="text-[12px] font-medium text-slate-700">
                          When do you plan to move out?
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="date"
                            value={noticeDate}
                            min={new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setNoticeDate(e.target.value)}
                            className="border border-slate-300 rounded-md px-2 py-1 text-[13px] focus:outline-none focus:border-blue-500"
                          />
                          <input
                            type="text"
                            value={noticeNote}
                            onChange={(e) => setNoticeNote(e.target.value)}
                            placeholder={t('Note (optional)')}
                            maxLength={500}
                            className="flex-1 min-w-[140px] border border-slate-300 rounded-md px-2 py-1 text-[13px] focus:outline-none focus:border-blue-500"
                          />
                          <button
                            onClick={() => giveMoveOutNotice(r._id)}
                            disabled={noticeBusy || !noticeDate}
                            className="rounded-lg bg-amber-600 text-white text-[12px] font-medium px-3 py-1.5 hover:bg-amber-700 disabled:opacity-50 transition-colors"
                          >
                            {noticeBusy ? t('Sending…') : t('Confirm notice')}
                          </button>
                          <button
                            onClick={() => { setNoticeFormRental(null); setNoticeError(null); }}
                            className="text-[12px] text-slate-400 hover:text-slate-600"
                          >
                            {t('Close')}
                          </button>
                        </div>
                        {noticeError && (
                          <p className="text-[12px] text-red-500">{noticeError}</p>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => { setNoticeFormRental(r._id); setNoticeDate(''); setNoticeNote(''); setNoticeError(null); }}
                        className="text-[12px] text-slate-400 hover:text-slate-600 font-medium"
                      >
                        {t('Give move-out notice')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
            {payments.length === 0 ? (
              <p className="px-4 py-8 text-center text-[13px] text-slate-400">{t('No payments found.')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/50 border-b border-slate-100">
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('Date')}</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('Amount')}</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('Method')}</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('Status')}</th>
                      <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('Receipt')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {payments.map((p) => (
                      <tr key={p._id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3 text-[13px] text-slate-600">{p.payment_date || p.created_at?.slice(0, 10) || '—'}</td>
                        <td className="px-4 py-3 text-[13px] font-semibold text-emerald-600">${(p.amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-[13px] text-slate-500">{p.payment_method || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                            p.status === 'completed' || p.status === 'succeeded' ? 'bg-emerald-50 text-emerald-600'
                              : p.status === 'processing' ? 'bg-blue-50 text-blue-600'
                              : p.status === 'failed' ? 'bg-red-50 text-red-600'
                              : 'bg-amber-50 text-amber-600'
                          }`}>
                            {p.status || 'recorded'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => downloadReceipt(p._id, p.payment_date || p.created_at?.slice(0, 10))}
                            className="text-[12px] text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {t('Download')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
            {invoices.length === 0 ? (
              <p className="px-4 py-8 text-center text-[13px] text-slate-400">{t('No invoices found.')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/50 border-b border-slate-100">
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Invoice #</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('Date')}</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Total</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('Status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {invoices.map((inv) => (
                      <tr
                        key={inv._id}
                        onClick={() => setSelectedInvoiceId(inv._id)}
                        className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-3 text-[13px] font-medium text-blue-600 hover:text-blue-700">
                          {inv.invoice_number || inv._id.slice(-6)}
                        </td>
                        <td className="px-4 py-3 text-[13px] text-slate-500">{inv.created_at?.slice(0, 10) || '—'}</td>
                        <td className="px-4 py-3 text-[13px] font-semibold text-slate-700">${(inv.total || 0).toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                            inv.status === 'paid' ? 'bg-emerald-50 text-emerald-600' :
                            inv.status === 'overdue' ? 'bg-red-50 text-red-500' :
                            'bg-amber-50 text-amber-600'
                          }`}>
                            {inv.status || 'pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-3">
            {documents === null ? (
              <div className="py-10 flex justify-center">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {documents.gate_note && (
                  <div className="rounded-xl border border-blue-200/80 bg-blue-50/60 p-4">
                    <p className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider mb-1">{t('Gate information')}</p>
                    <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{documents.gate_note}</p>
                  </div>
                )}
                <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
                  {(documents.documents || []).length === 0 ? (
                    <p className="px-4 py-8 text-center text-[13px] text-slate-400">{t('No documents yet.')}</p>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {documents.documents.map((d, i) => (
                        <li key={i} className="px-4 py-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-[13px] font-medium text-slate-900">{d.label}</p>
                            {d.date && <p className="text-[12px] text-slate-400">{d.date}</p>}
                          </div>
                          <button
                            onClick={() => d.kind === 'lease'
                              ? openLease(d.rental_id)
                              : downloadSignedDoc(d.doc_id, d.label)}
                            className="text-[12px] text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {d.kind === 'lease' ? t('View') : t('Download')}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="space-y-4 max-w-2xl">
            <div className="rounded-xl border border-slate-200/80 bg-white p-4">
              <h3 className="text-[14px] font-semibold text-slate-900 mb-1">{t('Message the office')}</h3>
              <p className="text-[12px] text-slate-500 mb-3">
                {t("We'll reply by email or text. For urgent gate or access issues, call the facility.")}
              </p>
              <input
                type="text"
                value={msgSubject}
                onChange={(e) => setMsgSubject(e.target.value)}
                placeholder={t('Subject (optional)')}
                maxLength={200}
                className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-2 focus:outline-none focus:border-blue-500"
              />
              <textarea
                value={msgBody}
                onChange={(e) => setMsgBody(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder={t('How can we help?')}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-blue-500"
              />
              <div className="flex items-center justify-between mt-2">
                {msgNotice ? (
                  <p className={`text-[12px] ${msgNotice.ok ? 'text-emerald-600' : 'text-red-500'}`}>{msgNotice.text}</p>
                ) : <span />}
                <button
                  onClick={sendMessage}
                  disabled={msgSending || !msgBody.trim()}
                  className="rounded-lg bg-blue-600 text-white text-[13px] font-medium px-4 py-2 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {msgSending ? t('Sending…') : t('Send message')}
                </button>
              </div>
            </div>
            {(messages || []).length > 0 && (
              <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
                <div className="px-4 py-2.5 border-b border-slate-100">
                  <h3 className="text-[13px] font-semibold text-slate-900">{t('Sent messages')}</h3>
                </div>
                <ul className="divide-y divide-slate-100">
                  {messages.map((m, i) => (
                    <li key={m.id || i} className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <p className="text-[13px] font-medium text-slate-900">{m.subject}</p>
                        <span className="text-[11px] text-slate-400">{(m.sent_at || '').slice(0, 10)}</span>
                      </div>
                      <p className="text-[12px] text-slate-500 whitespace-pre-wrap">{m.body}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="rounded-xl border border-slate-200/80 bg-white p-6">
            {profileForm === null ? (
              <p className="text-[13px] text-slate-400">Loading your info...</p>
            ) : (
              <>
                <div className="mb-5">
                  <h2 className="text-[14px] font-semibold text-slate-900">My Info</h2>
                  <p className="text-[12px] text-slate-400 mt-0.5">
                    Update the details we have on file. Name and account number are
                    set by the facility — call us if those need to change.
                  </p>
                </div>

                {/* Read-only identity */}
                <div className="grid gap-3 md:grid-cols-2 mb-5 pb-5 border-b border-slate-100">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1">Full name</label>
                    <p className="text-[13px] text-slate-700">{profile?.full_name || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1">Customer #</label>
                    <p className="text-[13px] text-slate-700">{profile?.customer_number || '—'}</p>
                  </div>
                </div>

                {/* Editable */}
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-1">Email</label>
                    <input
                      type="email"
                      value={profileForm.email || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={profileForm.phone || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[12px] font-medium text-slate-600 mb-1">Mailing address</label>
                    <input
                      type="text"
                      value={profileForm.address || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-1">City</label>
                    <input
                      type="text"
                      value={profileForm.city || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] font-medium text-slate-600 mb-1">State</label>
                      <input
                        type="text"
                        value={profileForm.state || ''}
                        onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-slate-600 mb-1">ZIP</label>
                      <input
                        type="text"
                        value={profileForm.zip_code || ''}
                        onChange={(e) => setProfileForm({ ...profileForm, zip_code: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-1">Emergency contact name</label>
                    <input
                      type="text"
                      value={profileForm.emergency_contact_name || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, emergency_contact_name: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-1">Emergency contact phone</label>
                    <input
                      type="tel"
                      value={profileForm.emergency_contact_phone || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, emergency_contact_phone: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[12px] font-medium text-slate-600 mb-1">Preferred contact method</label>
                    <select
                      value={profileForm.preferred_contact_method || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, preferred_contact_method: e.target.value || null })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    >
                      <option value="">No preference</option>
                      <option value="email">Email</option>
                      <option value="sms">Text (SMS)</option>
                    </select>
                  </div>
                </div>

                {profileMessage && (
                  <p className="mt-4 text-[12px] text-emerald-600">{profileMessage}</p>
                )}
                {profileError && (
                  <p className="mt-4 text-[12px] text-red-600">{profileError}</p>
                )}

                <div className="mt-5 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={saveProfile}
                    disabled={profileSaving}
                    className="rounded-lg bg-blue-600 text-white text-[13px] font-medium px-4 py-2 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {profileSaving ? 'Saving...' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setProfileForm(profile); setProfileMessage(null); setProfileError(null); }}
                    disabled={profileSaving}
                    className="text-[13px] text-slate-500 hover:text-slate-700 font-medium"
                  >
                    Reset
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {rentModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
          onClick={() => { if (!rentBusy) setRentModalOpen(false); }}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[15px] font-semibold text-slate-900 mb-1">
              {t('Rent another unit')}
            </h3>
            <p className="text-[12px] text-slate-500 mb-4">
              {t('Pick a unit — you will sign the lease, then pay, and your gate code arrives by email.')}
            </p>

            {rentError && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-[12px]">{rentError}</div>
            )}

            {!rentOptions && !rentError && (
              <p className="text-[13px] text-slate-400 py-6 text-center">…</p>
            )}

            {rentOptions && rentOptions.unit_types.length === 0 && (
              <p className="text-[13px] text-slate-500 py-4 text-center">
                {t('No units are available right now — contact the office.')}
              </p>
            )}

            {rentOptions && rentOptions.unit_types.length > 0 && (
              <>
                <div className="space-y-2 max-h-[300px] overflow-y-auto mb-4">
                  {rentOptions.unit_types.map((ut) => (
                    <button
                      key={ut.id}
                      type="button"
                      onClick={() => {
                        setRentTypeId(ut.id);
                        setRentUnitId(ut.units?.[0]?.id || '');
                      }}
                      className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                        rentTypeId === ut.id
                          ? 'border-blue-500 bg-blue-50/60'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-medium text-slate-800">
                          {ut.name}
                          {ut.width && ut.length ? ` (${ut.width}×${ut.length})` : ''}
                        </span>
                        <span className="text-[13px] font-semibold text-slate-900">
                          ${ut.monthly_rate}/mo
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {ut.available} {t('available')}
                      </p>
                    </button>
                  ))}
                </div>

                {(() => {
                  const sel = rentOptions.unit_types.find((x) => x.id === rentTypeId);
                  if (!sel) return null;
                  return (
                    <select
                      value={rentUnitId}
                      onChange={(e) => setRentUnitId(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-[13px] mb-4"
                    >
                      <option value="">{t('Choose unit…')}</option>
                      {sel.units.map((u) => (
                        <option key={u.id} value={u.id}>Unit {u.unit_number}</option>
                      ))}
                    </select>
                  );
                })()}

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setRentModalOpen(false)}
                    disabled={rentBusy}
                    className="px-3.5 py-2 rounded-lg bg-blue-600 text-[13px] font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {t('Close')}
                  </button>
                  <button
                    type="button"
                    onClick={startRent}
                    disabled={rentBusy || !rentUnitId}
                    className="px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50"
                  >
                    {rentBusy ? t('Starting…') : t('Continue to lease')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {selectedInvoiceId && (
        <InvoiceDetailModal
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
          onPay={(inv) => {
            setSelectedInvoiceId(null);
            // Route to the existing portal pay page with the invoice's
            // outstanding total prefilled. The pay page reads rental from
            // the rental_id when present.
            const amt = (inv?.total || 0).toFixed(2);
            const rid = inv?.rental_id || '';
            navigate(`/portal/pay?rental=${rid}&amount=${amt}`);
          }}
        />
      )}
    </div>
  );
};

export default PortalDashboard;
